import { useState } from "react";
import { Box } from "@mui/material";
import ChartCard, { Segmented } from "./ChartCard";
import { COLORS, HEAT, fmtCount } from "./emsTheme";

// Age bands of open + pending tickets (from creation). from/to are days, for the drill-down.
export const BANDS = [
    { key: "under1Day", label: "Under 1 day", from: null, to: 1 },
    { key: "days1To3", label: "1 to 3 days", from: 1, to: 3 },
    { key: "days3To7", label: "3 to 7 days", from: 3, to: 7 },
    { key: "days7To15", label: "7 to 15 days", from: 7, to: 15 },
    { key: "over15Days", label: "15+ days", from: 15, to: null },
];
const VIEWS = [
    { key: "ageingByCategory", label: "Category", filterField: "category" },
    { key: "ageingByMerchant", label: "Merchant", filterField: "merchantId" },
];

const shade = (v, max) => {
    if (!v) return null;
    const i = Math.min(HEAT.length - 1, Math.floor((v / max) * (HEAT.length - 1) + 0.0001));
    return HEAT[i];
};

/** The title, from the data: how much open work is older than a week. */
export const ageingTitle = (rows) => {
    const old = (rows || []).reduce((a, r) => a + (r.days7To15 || 0) + (r.over15Days || 0), 0);
    if (!rows || !rows.length) return "Open tickets by age";
    return old ? `${fmtCount(old)} open ${old === 1 ? "ticket is" : "tickets are"} older than 7 days` : "No open ticket is older than 7 days";
};

/**
 * Ageing heatmap: open + pending tickets by age band, per category or merchant. Darker purple =
 * more tickets. Every cell opens exactly those tickets.
 */
const AgeingCard = ({ stats, loading, error, onRetry, chips, updatedLabel, onDrilldown }) => {
    const views = VIEWS.filter((v) => stats?.[v.key] != null); // merchant logins don't get the merchant view
    const [viewKey, setViewKey] = useState("ageingByCategory");
    const view = VIEWS.find((v) => v.key === viewKey) || VIEWS[0];
    const rows = (stats?.[view.key] || []).filter(Boolean);
    const max = Math.max(1, ...rows.flatMap((r) => BANDS.map((b) => r[b.key] || 0)));

    const drill = (row, band) => {
        const patch = { unresolvedOnly: true };
        if (row?.key) patch[view.filterField] = row.key;
        if (band) {
            if (band.from != null) patch.ageFromDays = band.from;
            if (band.to != null) patch.ageToDays = band.to;
        }
        onDrilldown(patch);
    };

    return (
        <ChartCard
            eyebrow={`Ageing by ${view.label.toLowerCase()}`}
            title={ageingTitle(stats?.ageingByCategory)}
            subtitle="Open and pending tickets by days since they were raised. Darker means more. Click a number to open those tickets."
            info="Age = now minus the ticket's created time. Counts open and pending tickets created in the selected range. Bands are by days since creation."
            chips={chips} loading={loading} error={error} onRetry={onRetry} empty={!loading && !error && rows.length === 0}
            emptyHint="No open tickets in this range." updatedLabel={updatedLabel} height={220} fileName="ems-ageing"
            ariaSummary={`${ageingTitle(stats?.ageingByCategory)}. Heatmap of open tickets by ${view.label.toLowerCase()} and age band.`}
            table={{
                columns: [{ key: "label", label: view.label }, ...BANDS.map((b) => ({ key: b.key, label: b.label, align: "right", text: (r) => fmtCount(r[b.key] || 0) })),
                    { key: "totalCount", label: "Total", align: "right", text: (r) => fmtCount(r.totalCount) }],
                rows,
            }}
            actions={views.length > 1 && (
                <Segmented label="Group by" value={view.key} onChange={setViewKey} options={views.map((v) => ({ value: v.key, label: v.label }))} />
            )}>
            {/* Scrolls inside the card once there are more rows than fit, header stays put. */}
            <Box sx={{ overflow: "auto", maxHeight: 420 }}>
                <Box component="table" sx={{ width: "100%", minWidth: 520, borderCollapse: "separate", borderSpacing: "3px", fontSize: 13,
                    "& th": { position: "sticky", top: 0, zIndex: 1, bgcolor: COLORS.surface, color: COLORS.ink3, fontWeight: 600, fontSize: 12, textAlign: "right", px: 1, py: 0.5, whiteSpace: "nowrap" },
                    "& th:first-of-type": { textAlign: "left" } }}>
                    <thead><tr><th>{view.label}</th>{BANDS.map((b) => <th key={b.key}>{b.label}</th>)}<th>Total</th></tr></thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.key || row.label}>
                                <Box component="td" title={row.label} sx={{ color: COLORS.ink, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 1 }}>{row.label}</Box>
                                {BANDS.map((b) => {
                                    const v = row[b.key] || 0;
                                    const s = shade(v, max);
                                    return (
                                        <td key={b.key} style={{ padding: 0 }}>
                                            <Box component={v ? "button" : "div"} type={v ? "button" : undefined} onClick={v ? () => drill(row, b) : undefined}
                                                aria-label={v ? `${row.label}, ${b.label}: ${v} open tickets. Open these tickets` : undefined}
                                                sx={{ all: "unset", boxSizing: "border-box", display: "block", width: "100%", textAlign: "right", px: 1, py: 0.6, borderRadius: "4px",
                                                    fontVariantNumeric: "tabular-nums", bgcolor: s ? s.bg : "transparent", color: s ? s.fg : COLORS.context,
                                                    fontWeight: v ? 600 : 400, cursor: v ? "pointer" : "default",
                                                    "&:focus-visible": { outline: `2px solid ${COLORS.orange}`, outlineOffset: 1 },
                                                    "&:hover": v ? { outline: `1px solid ${COLORS.purple}` } : {} }}>
                                                {fmtCount(v)}
                                            </Box>
                                        </td>
                                    );
                                })}
                                <td style={{ textAlign: "right", paddingRight: 8 }}>
                                    <Box component="button" type="button" onClick={() => drill(row, null)} aria-label={`${row.label}: ${row.totalCount} open tickets. Open these tickets`}
                                        sx={{ all: "unset", cursor: "pointer", fontWeight: 700, color: COLORS.ink, fontVariantNumeric: "tabular-nums", "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                        {fmtCount(row.totalCount)}
                                    </Box>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </Box>
        </ChartCard>
    );
};

export default AgeingCard;
